// Install modal for marketplace integration / registrar kinds.
//
// Triggered by clicking "Install" on any integration or registrar tile,
// or by `el.call('openInstallableModal', opts)` from any surface.
// Keeps the user on the current page (no full-page navigation) and
// walks through:
//
//   Step 1 — Scope selection (org / workspace / project). Auto-skips
//   steps when the user has a single org or the chosen org has a single
//   workspace. Project selection is always optional. Skipped entirely
//   when `defaultScope` is pre-filled by the caller.
//
//   Step 2 — Configuration form (integration-specific fields + API key).
//   Uses IntegrationScopePicker (shared) for scope and mirrors the
//   GenericIntNewForm field pattern for config fields.
//
// Phase 4 extensibility props (NEW):
//   preselectedKind   – string slug — opens modal pre-selected on this kind,
//                       skipping the browse step. Validated against live
//                       catalogue at mount; error shown when not found.
//   defaultScope      – { type: 'org'|'workspace'|'project', id: string } —
//                       pre-fills the scope picker, skipping manual selection.
//   catalogFilter     – { category?, applicable_to_types?, applicable_to_scopes? }
//                       — constrains which catalogue kinds the user can pick.
//                       If preselectedKind violates catalogFilter, error is shown.
//   onInstalled       – (instance) => void — callback fired after a successful
//                       install (optimistic). The instance is the raw API
//                       response body. Roll-back happens if the server later
//                       rejects.
//   domain            – 'integration'|'library'|'mcp'|'plugin'|'template' —
//                       used by domain-aware write target selection in
//                       `submitInstallModal`.
//
// State lives on the marketplace host (island or canvas page):
//   host.installModal: null | { item, step:'scope'|'config', draft:{},
//                               preselectedKind?, defaultScope?,
//                               catalogFilter?, onInstalled?, domain? }
//   host._installModalSaving: bool
//   host._installModalError: string | null
//
// The modal is gated by `host.installModal` (null → hidden) via a root
// reactive `display:` predicate on MarketplaceInstallModal itself — the
// SAME house overlay pattern MarketplaceSigninModal / MarketplaceDetailModal
// use (fixed inset, black.45 backdrop, centered flex, Esc + backdrop-click
// close). NOTE: this deliberately uses `display:` (returns 'flex' | 'none'),
// NOT `hide:`. An earlier revision gated with `hide:`, but a `hide:`d root
// is short-circuited out of the render tree and never re-evaluates its own
// predicate, so once mounted-closed it stuck at `display: none !important`
// forever — the backdrop + card never painted and the subtree stayed
// frozen (the "no background, unstyled floating block" bug). A `display:`
// predicate is a plain reactive style prop that always re-fires on host
// state changes, so the overlay shows/hides reliably. `display: 'none'`
// when closed also prevents any un-gated ModalTop/ModalName/CancelBtn
// fragment from leaking at rest — nothing inside can paint while the root
// is `none`, full stop.
//
// All functions are accessed via el.call('fnName', ...) per CLAUDE.md
// rule — no imports between project files.

// ── Scope wizard helpers ────────────────────────────────────────────────

// Resolve the org list. Marketplace mirrors _authClaims; governance
// exposes jwtClaims.orgs. Try both.
const _getOrgs = (host) => {
  const claims = host?._authClaims || host?.root?._authClaims || {}
  const jwtOrgs = host?.root?.jwtClaims?.orgs || []
  const metaOrg = claims.workspace_org_id
    ? [{ id: claims.workspace_org_id, name: claims.workspace_org_id }]
    : []
  return jwtOrgs.length ? jwtOrgs : metaOrg
}

// Resolve workspace list — the island host's own hydrated list first
// (ensureWorkspacesForAppPicker writes it for kind='app' installs), then
// the legacy root/claims sources (IntegrationScopePicker parity).
const _getWorkspaces = (host) => {
  const orgId = host?._authClaims?.workspace_org_id || host?.root?.jwtClaims?.workspace_org_id
  const list =
    Array.isArray(host?.workspaces) && host.workspaces.length
      ? host.workspaces
      : Array.isArray(host?.root?.workspaces)
        ? host.root.workspaces
        : host?.root?.jwtClaims?.workspaces || []
  return orgId ? list.filter((w) => !w.org_id || w.org_id === orgId) : list
}

// Resolve project list — same dual-source as IntegrationScopePicker.
const _getProjects = (host) => {
  const orgId = host?._authClaims?.workspace_org_id || host?.root?.jwtClaims?.workspace_org_id
  if (Array.isArray(host?.root?.projects) && host.root.projects.length) {
    return host.root.projects.map((p) => ({
      id: p.id || p._id || p.key,
      key: p.key || p.slug,
      name: p.name || p.displayName || p.key
    }))
  }
  if (orgId) {
    const cache = (host?.root?._scopeProjectsByOrg || {})[orgId]
    if (Array.isArray(cache)) return cache
  }
  return []
}

// ── Config field (mirrors GenericIntConfigField but reads from installModal.draft) ──
//
// The shared field kit (workspace-shared AppField.js): AppField owns the
// caption label, AppInput the control surface; this field keeps the
// behaviour only. The label is the schema field's own (server) label read off
// the field's state, with the required mark — a FUNCTION prop, so the kit's
// FieldLabel and the control's accessible name both read it live.
export const MarketplaceInstallConfigField = {
  extends: 'AppField',
  label: (el, s) => {
    const req = s.required ? ' *' : ''
    return (s.label || s.key || '') + req
  },

  AppInput: {
    type: (el, s) =>
      s.key && (s.key.includes('secret') || s.key.includes('key') || s.key.includes('token'))
        ? 'password'
        : 'text',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => {
      const host = el.call('marketplaceHostOf')
      return host?.installModal?.draft?.[s.key] || ''
    },
    onInput: (e, el, s) => {
      const host = el.call('marketplaceHostOf')
      if (!host?.installModal) return
      host.update?.({
        installModal: {
          ...host.installModal,
          draft: {
            ...(host.installModal.draft || {}),
            [s.key]: e.target.value
          }
        }
      })
    }
  }
}

// ── Catalog validation error (preselectedKind not found / violates catalogFilter) ──

// The danger surface of the redesign (DESIGN_SPEC §5.8): the `dangerInk` pair
// on a wash of that same ink — no raw hue fill, no red hairline — announced.
export const MarketplaceInstallCatalogError = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'radiusControl',
  color: 'dangerInk',
  background: 'color-mix(in srgb, currentColor 10%, transparent)',
  role: 'alert',
  hide: (el) => {
    const host = el.call('marketplaceHostOf')
    return !host?.installModal?._catalogError
  },

  ErrorIcon: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'currentColor',
    text: '!'
  },

  ErrorText: {
    tag: 'p',
    fontSize: 'Y',
    color: 'currentColor',
    margin: '0',
    text: (el) => {
      const host = el.call('marketplaceHostOf')
      return host?.installModal?._catalogError || ''
    }
  }
}

// ── Scope step ─────────────────────────────────────────────────────────

export const MarketplaceInstallScopeStep = {
  flow: 'y',
  gap: 'A',

  ScopeTitle: {
    tag: 'h4',
    text: (el) => {
      const host = el.call('marketplaceHostOf')
      return el.call(
        'polyglot',
        host?.installModal?.item?.kind === 'app'
          ? 'marketplace.install.scopeTitleApp'
          : 'marketplace.install.scopeTitle'
      )
    },
    fontSize: 'Z',
    fontWeight: '700',
    color: 'title',
    margin: '0'
  },

  ScopeHint: {
    tag: 'p',
    fontSize: 'Y',
    color: 'caption',
    margin: '0',
    text: (el) => {
      const host = el.call('marketplaceHostOf')
      return el.call(
        'polyglot',
        host?.installModal?.item?.kind === 'app'
          ? 'marketplace.install.scopeHintApp'
          : 'marketplace.install.scopeHint'
      )
    }
  },

  // Org-missing guard: user has no org → block with message.
  NoOrgWarning: {
    flow: 'y',
    gap: 'Z',
    padding: 'A',
    round: 'radiusControl',
    // The danger surface: the `dangerInk` pair on a wash of itself (it was
    // a raw `red.1` fill under raw `red` ink).
    color: 'dangerInk',
    background: 'color-mix(in srgb, currentColor 10%, transparent)',
    role: 'alert',
    hide: (el) => {
      const host = el.call('marketplaceHostOf')
      const orgs = _getOrgs(host)
      return orgs.length > 0
    },

    NoOrgText: {
      tag: 'p',
      fontSize: 'Y',
      color: 'currentColor',
      margin: '0',
      text: '{{ marketplace.install.noOrg | polyglot }}'
    }
  },

  ScopeForm: {
    flow: 'y',
    gap: 'A',
    hide: (el) => {
      const host = el.call('marketplaceHostOf')
      const orgs = _getOrgs(host)
      return orgs.length === 0
    },

    // Scope mode buttons (org / workspace / project) — mirrors IntegrationScopePicker.Modes.
    // 3 independent pill buttons (not a joined segmented track), so wrapping
    // onto a second line is a clean reflow rather than a broken shape —
    // "Project (optional)" alone pushed this row past a narrow modal step.
    ScopeModes: {
      flow: 'x',
      gap: 'Z',
      wrap: 'wrap',
      rowGap: 'Z',

      ScopeModeOrg: {
        extends: 'Button',
        text: '{{ marketplace.install.scopeOrg | polyglot }}',
        minHeight: 'controlHCompact',
        padding: '0 A',
        round: 'radiusPill',
        // Workspace apps install directly into a workspace — never org
        // scope (WORKSPACE_APPS.md "Install": "scope constrained to
        // workspace").
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          return host?.installModal?.item?.kind === 'app'
        },
        // Active-mode fill converges on the house pattern (`selected` +
        // title — DESIGN_LANGUAGE.md §4.3, non-negotiable) instead of a
        // saturated blue fill.
        background: (el) => {
          const host = el.call('marketplaceHostOf')
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'org' ? 'selected' : 'transparent'
        },
        color: 'title',
        borderColor: 'line',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          if (!host?.installModal) return
          host.update?.({
            installModal: {
              ...host.installModal,
              draft: { ...(host.installModal.draft || {}), scopeType: 'org', scopeId: null }
            },
            _scopeWsQuery: '',
            _scopeProjQuery: ''
          })
        }
      },

      ScopeModeWorkspace: {
        extends: 'Button',
        text: '{{ marketplace.install.scopeWorkspace | polyglot }}',
        minHeight: 'controlHCompact',
        padding: '0 A',
        round: 'radiusPill',
        background: (el) => {
          const host = el.call('marketplaceHostOf')
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'workspace' ? 'selected' : 'transparent'
        },
        color: 'title',
        borderColor: 'line',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          if (!host?.installModal) return
          host.update?.({
            installModal: {
              ...host.installModal,
              draft: {
                ...(host.installModal.draft || {}),
                scopeType: 'workspace',
                scopeId: (() => {
                  const ws = _getWorkspaces(host)
                  return ws.length === 1 ? ws[0].id || ws[0].workspace_id || ws[0]._id : null
                })()
              }
            },
            _scopeWsQuery: ''
          })
        }
      },

      ScopeModeProject: {
        extends: 'Button',
        text: '{{ marketplace.install.scopeProject | polyglot }}',
        minHeight: 'controlHCompact',
        padding: '0 A',
        round: 'radiusPill',
        // Same constraint as ScopeModeOrg above — apps never scope to
        // a project.
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          return host?.installModal?.item?.kind === 'app'
        },
        background: (el) => {
          const host = el.call('marketplaceHostOf')
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'project' ? 'selected' : 'transparent'
        },
        color: 'title',
        borderColor: 'line',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          if (!host?.installModal) return
          host.update?.({
            installModal: {
              ...host.installModal,
              draft: { ...(host.installModal.draft || {}), scopeType: 'project', scopeId: null }
            },
            _scopeProjQuery: ''
          })
        }
      }
    },

    // Workspace picker — visible when scopeType === 'workspace'.
    WorkspacePicker: {
      flow: 'y',
      gap: 'Z',
      hide: (el) => {
        const host = el.call('marketplaceHostOf')
        return (host?.installModal?.draft?.scopeType || 'org') !== 'workspace'
      },

      WorkspacePickerLabel: {
        tag: 'label',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '700',
        text: '{{ marketplace.install.selectWorkspace | polyglot }}'
      },

      // A listbox on the kit's FIELD surface (it is an input); the rows sit
      // on it flat, apart by a gap — no hairlines inside a rounded box.
      WorkspaceList: {
        flow: 'y',
        gap: 'V',
        padding: 'X',
        maxHeight: 'E',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        round: 'radiusControl',
        theme: 'field',

        childrenAs: 'state',
        // Flat rows on the listbox (the gap separates them — no hairlines
        // inside the rounded box), the selected row on the `selected` wash
        // with the ink check (WsCheck below).
        childExtends: {
          tag: 'button',
          type: 'button',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 'Z',
          align: 'center left',
          width: '100%',
          padding: 'Y Z1',
          round: 'radiusControl',
          border: 'none',
          // Selected-row fill converges on the house active pattern
          // (`selected` + title, DESIGN_LANGUAGE.md §4.3) — hover on the
          // already-selected row stays `selected` too (not the neutral
          // `hover` wash) so a selected row still doesn't change appearance
          // on hover.
          background: (el, s) => (s._selected ? 'selected' : 'transparent'),
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          textAlign: 'left',
          ':hover': { background: (el, s) => (s._selected ? 'selected' : 'hover') },

          // The workspace's NAME — primary text, never cut (the overflow
          // law, HOME_AMBIENT_SKIN_SPEC §3b rule 8): it wraps beside the check.
          WsLabel: {
            tag: 'span',
            minWidth: '0',
            overflowWrap: 'anywhere',
            text: (el, s) => s._label || ''
          },
          // House `Icon` replaces the old ✓ text glyph (Rule 29).
          WsCheck: {
            extends: 'Icon',
            name: 'check',
            fontSize: 'Y1',
            // Selection is monochrome (P5) — the check in ink.
            color: 'title',
            hide: (el, s) => !s._selected
          },

          onClick: (e, el, s) => {
            const host = el.call('marketplaceHostOf')
            if (!host?.installModal) return
            host.update?.({
              installModal: {
                ...host.installModal,
                draft: {
                  ...(host.installModal.draft || {}),
                  scopeType: 'workspace',
                  scopeId: s._value || null
                }
              }
            })
          }
        },
        children: (el) => {
          const host = el.call('marketplaceHostOf')
          const curId = host?.installModal?.draft?.scopeId || ''
          const ws = _getWorkspaces(host).map((w) => ({
            id: w.id || w.workspace_id || w._id,
            name: w.name || w.id || w.workspace_id
          }))
          if (!ws.length) return []
          return ws.map((w) => ({
            _value: w.id,
            _label: w.name,
            _selected: w.id === curId
          }))
        },

        WorkspaceEmpty: {
          tag: 'span',
          padding: 'V Z1',
          fontSize: 'Z2',
          color: 'caption',
          fontStyle: 'italic',
          hide: (el) => {
            const host = el.call('marketplaceHostOf')
            return _getWorkspaces(host).length > 0
          },
          text: '{{ marketplace.install.noWorkspaces | polyglot }}'
        }
      }
    },

    // Project picker — visible when scopeType === 'project'.
    ProjectPicker: {
      flow: 'y',
      gap: 'Z',
      hide: (el) => {
        const host = el.call('marketplaceHostOf')
        return (host?.installModal?.draft?.scopeType || 'org') !== 'project'
      },

      ProjectPickerLabel: {
        tag: 'label',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '700',
        text: '{{ marketplace.install.selectProject | polyglot }}'
      },

      // A listbox on the kit's FIELD surface (it is an input); the rows sit
      // on it flat, apart by a gap — no hairlines inside a rounded box.
      ProjectList: {
        flow: 'y',
        gap: 'V',
        padding: 'X',
        maxHeight: 'E',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        round: 'radiusControl',
        theme: 'field',

        childrenAs: 'state',
        // The same flat-row + ink-check pattern as WorkspaceList above.
        childExtends: {
          tag: 'button',
          type: 'button',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 'Z',
          align: 'center left',
          width: '100%',
          padding: 'Y Z1',
          round: 'radiusControl',
          border: 'none',
          // Selected-row fill converges on the house active pattern
          // (`selected` + title, DESIGN_LANGUAGE.md §4.3) — hover on the
          // already-selected row stays `selected` too (not the neutral
          // `hover` wash) so a selected row still doesn't change appearance
          // on hover.
          background: (el, s) => (s._selected ? 'selected' : 'transparent'),
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          textAlign: 'left',
          ':hover': { background: (el, s) => (s._selected ? 'selected' : 'hover') },

          // The project's NAME — primary text, never cut (overflow law
          // rule 8): it wraps beside the check.
          ProjLabel: {
            tag: 'span',
            minWidth: '0',
            overflowWrap: 'anywhere',
            text: (el, s) => s._label || ''
          },
          // House `Icon` replaces the old ✓ text glyph (Rule 29).
          ProjCheck: {
            extends: 'Icon',
            name: 'check',
            fontSize: 'Y1',
            // Selection is monochrome (P5) — the check in ink.
            color: 'title',
            hide: (el, s) => !s._selected
          },

          onClick: (e, el, s) => {
            const host = el.call('marketplaceHostOf')
            if (!host?.installModal) return
            const curId = host.installModal.draft?.scopeId || ''
            // Clicking the already-selected project deselects (no project).
            const nextId = s._value === curId ? null : s._value || null
            host.update?.({
              installModal: {
                ...host.installModal,
                draft: {
                  ...(host.installModal.draft || {}),
                  scopeType: 'project',
                  scopeId: nextId
                }
              }
            })
          }
        },
        children: (el) => {
          const host = el.call('marketplaceHostOf')
          const curId = host?.installModal?.draft?.scopeId || ''
          const projects = _getProjects(host)
          if (!projects.length) return []
          return projects.map((p) => ({
            _value: p.id || p.key,
            _label: p.name || p.key || p.id,
            _selected: (p.id || p.key) === curId
          }))
        },

        ProjectEmpty: {
          tag: 'span',
          padding: 'V Z1',
          fontSize: 'Z2',
          color: 'caption',
          fontStyle: 'italic',
          hide: (el) => {
            const host = el.call('marketplaceHostOf')
            return _getProjects(host).length > 0
          },
          text: '{{ marketplace.install.noProjects | polyglot }}'
        }
      }
    }
  }
}

// ── Config step ─────────────────────────────────────────────────────────

export const MarketplaceInstallConfigStep = {
  flow: 'y',
  gap: 'A',

  ConfigTitle: {
    tag: 'h4',
    text: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      return el
        .call('polyglot', 'marketplace.install.configureTitle')
        .replace(
          '{name}',
          el.call('storeItemText', item, 'name') ||
            el.call('polyglot', 'marketplace.install.genericIntegration')
        )
    },
    fontSize: 'Z',
    fontWeight: '700',
    color: 'title',
    margin: '0'
  },

  // Display name field — only shown when allows_multiple. The shared field
  // kit, like the config fields above; `label` is a FUNCTION prop — the kit
  // also names the control with it, and an attribute never resolves a
  // `{{ }}` template.
  DisplayNameField: {
    extends: 'AppField',
    hide: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      return !item?._liveKind?.allows_multiple
    },
    label: (el) => el.call('polyglot', 'marketplace.install.displayName'),

    AppInput: {
      type: 'text',
      // The kind record's own label first; the bundle only as the fallback.
      placeholder: (el) => {
        const host = el.call('marketplaceHostOf')
        return (
          host?.installModal?.item?._liveKind?.label ||
          el.call('polyglot', 'marketplace.install.displayNamePlaceholder')
        )
      },
      value: (el) => {
        const host = el.call('marketplaceHostOf')
        return host?.installModal?.draft?.displayName || ''
      },
      onInput: (e, el) => {
        const host = el.call('marketplaceHostOf')
        if (!host?.installModal) return
        host.update?.({
          installModal: {
            ...host.installModal,
            draft: { ...(host.installModal.draft || {}), displayName: e.target.value }
          }
        })
      }
    }
  },

  // Dynamic config fields from config_schema.fields. Belt-and-braces gate
  // (WS-APPS ticket "audit inner hide-gates for at-rest leakage"): the
  // enclosing MarketplaceInstallConfigStep already hides for
  // `step !== 'config'` at its own call site, but this container now also
  // checks the step directly so MarketplaceInstallConfigField never
  // renders (with a blank draft) outside the config step even if this
  // component is ever mounted somewhere that doesn't wrap it that way.
  DynamicConfigFields: {
    flow: 'y',
    gap: 'A',
    hide: (el) => {
      const host = el.call('marketplaceHostOf')
      const modal = host?.installModal
      if (!modal || (modal.step || 'scope') !== 'config') return true
      const item = modal.item
      const fields = (item?._liveKind?.config_schema?.fields || []).filter(
        (f) => f.key !== 'display_name'
      )
      return fields.length === 0
    },
    childrenAs: 'state',
    childExtends: 'MarketplaceInstallConfigField',
    children: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      return (item?._liveKind?.config_schema?.fields || []).filter((f) => f.key !== 'display_name')
    }
  },

  // API key / secret field — hidden for oauth2 / webhook / none auth models.
  // The shared field kit: the kind record's own secret label is the field
  // label, and its secret hint rides the kit's hint line under the control
  // (the kit hides the line while the hint is empty).
  SecretField: {
    extends: 'AppField',
    hide: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      const kr = item?._liveKind
      if (!kr || kr.auth_model === 'none') return true
      const flow = kr.ui_flow || 'credential'
      return flow === 'oauth2' || flow === 'webhook'
    },
    label: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      return item?._liveKind?.secret_label || el.call('polyglot', 'marketplace.install.secretLabel')
    },
    hint: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      return item?._liveKind?.secret_hint || ''
    },

    AppInput: {
      type: 'password',
      placeholder: '••••••••••••••••',
      value: (el) => {
        const host = el.call('marketplaceHostOf')
        return host?.installModal?.draft?.secret || ''
      },
      onInput: (e, el) => {
        const host = el.call('marketplaceHostOf')
        if (!host?.installModal) return
        host.update?.({
          installModal: {
            ...host.installModal,
            draft: { ...(host.installModal.draft || {}), secret: e.target.value }
          }
        })
      }
    }
  },

  // No-config notice for integrations that need no configuration.
  NoConfigNotice: {
    tag: 'p',
    fontSize: 'Y',
    color: 'caption',
    margin: '0',
    fontStyle: 'italic',
    hide: (el) => {
      const host = el.call('marketplaceHostOf')
      const item = host?.installModal?.item
      const kr = item?._liveKind
      if (!kr) return false
      const fields = (kr.config_schema?.fields || []).filter((f) => f.key !== 'display_name')
      const authModel = kr.auth_model || 'none'
      const flow = kr.ui_flow || 'credential'
      const needsSecret = authModel !== 'none' && flow !== 'oauth2' && flow !== 'webhook'
      return fields.length > 0 || needsSecret
    },
    text: '{{ marketplace.install.noConfig | polyglot }}'
  }
}

// ── Main modal ──────────────────────────────────────────────────────────

// Root overlay shell (WS-APPS fix) — mirrors MarketplaceSigninModal /
// MarketplaceDetailModal's exact house pattern: fixed inset + centered
// flex + black.45 scrim backdrop + Esc-close + backdrop-click-close.
// The root gate is a reactive `display:` predicate (returns 'flex' | 'none'),
// matching both siblings — NOT `hide:`. A `hide:`d root is short-circuited
// out of the render tree and never re-fires its own predicate, so it stuck
// at `display: none !important` after mounting closed and never reopened;
// a `display:` predicate re-evaluates on every host-state change, so open/
// close is reliable and `display: 'none'` when closed still stops every
// un-gated fragment from leaking at rest. Setters across
// shared/functions/installable.js, MarketplaceIsland.js,
// canvas/functions/canvas/installMarketplaceItem.js keep using
// `host.update({ installModal: … })` unchanged — no call-site migrations.
export const MarketplaceInstallModal = {
  // House overlay pattern — a REACTIVE `display:` predicate, exactly like
  // the two working siblings (MarketplaceSigninModal / MarketplaceDetailModal),
  // NOT `hide:`. Root cause of the "no backdrop, unstyled floating block"
  // report: `hide:` short-circuits the root out of the render tree while
  // `installModal` is null, and a hidden root never re-evaluates its OWN
  // `hide` predicate — so it stayed `display: none !important` forever once
  // mounted-closed, and the whole subtree stayed frozen even after
  // `installModal` was set. `display` is a plain reactive style prop that
  // re-fires on every host-state change regardless of current visibility
  // (the siblings prove it opens/closes reliably), so the overlay paints
  // and the subtree goes live. When shown it is `flex`, which activates the
  // `align: 'center center'` centering below.
  display: (el) => {
    const host = el.call('marketplaceHostOf')
    return host?.installModal ? 'flex' : 'none'
  },
  position: 'fixed',
  inset: '0',
  // Above MarketplaceDetailModal (1900) — the install flow can open on
  // top of an already-open detail sheet (dispatchInstallWithScope /
  // ActionGroup's ScopeMenu don't close the detail modal first) — and
  // below MarketplaceSigninModal (2001), which supersedes it entirely
  // when a logged-out visitor needs to authenticate first.
  zIndex: '1950',
  align: 'center center',
  // THE one blocking veil (DESIGN_SPEC §6.3) — the `backdrop` theme, fading
  // in as the root flips to flex; same as MarketplaceDetailModal (it was a
  // raw theme-invariant `black.45` + a 4px blur of its own).
  theme: 'backdrop',
  animationName: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'both',
  // THE EXIT (closeInstallModal -> marketplaceCloseWithExit), same as
  // MarketplaceDetailModal: the veil fades out, the window leaves on
  // `modalOut`, then the state clears.
  dataClosing: (el) => !!el.call('marketplaceHostOf')?._installModalClosing,
  '&[data-closing]': {
    animationName: 'fadeOut',
    animationDuration: 'B',
    pointerEvents: 'none'
  },
  padding: 'A',

  onKeydown: (e, el) => {
    if (e.key !== 'Escape') return
    const host = el.call('marketplaceHostOf')
    el.call('closeInstallModal', host)
    e.preventDefault()
  },

  onClick: (e, el) => {
    if (e.target !== el.node) return
    const host = el.call('marketplaceHostOf')
    el.call('closeInstallModal', host)
  },

  ModalWindow: {
    // Brand's ModalWindow auto-mounts a `ModalHeader: {}` slot (an empty
    // Hgroup/H/P + a redundant second close X) — same fix
    // MarketplaceDetailModal.js's Window already applies; this file was
    // missing it, which doubled up on top of the ModalTop header below.
    ModalHeader: null,
    // The overlay SHEET (DESIGN_SPEC §6.2): the opaque `dialog` THEME (never
    // `background: 'dialog'` — a theme name in a colour slot paints nothing),
    // `radiusSheet`, the `overlay` elevation, which carries the ring — so no
    // hairline border (borderless law). It rises on the spring as the veil
    // fades in (`modalIn`, D springBezier).
    theme: 'dialog',
    flow: 'y',
    gap: 'A',
    padding: 'B',
    round: 'radiusSheet',
    minWidth: 'G',
    // The form width of the overlay scale (`overlayMd`, 480px — fixed, never
    // em: the sheet must not scale with the font of what it floats over).
    maxWidth: 'overlayMd',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    // Vertical scroll only. The full-bleed ModalFooter
    // (negative `margin: 0 -B` to reach the card edges) otherwise bleed a
    // hair past the box and raise a spurious horizontal scrollbar at this
    // width — clip it so the card never scrolls sideways.
    overflowX: 'hidden',
    shadow: 'overlay',
    animationName: 'modalIn',
    animationDuration: 'D',
    animationTimingFunction: 'springBezier',
    animationFillMode: 'both',
    dataClosing: (el) => !!el.call('marketplaceHostOf')?._installModalClosing,
    '&[data-closing]': {
      animationName: 'modalOut',
      animationDuration: 'C',
      animationTimingFunction: 'defaultBezier'
    },
    // Tab-trapping for a11y — focus stays inside the modal.
    tabindex: '-1',

    // Header: integration logo + name + close button.
    ModalTop: {
      flow: 'x',
      align: 'center space-between',
      gap: 'A',

      ModalTitleRow: {
        flow: 'x',
        align: 'center',
        gap: 'Z',

        ModalIcon: {
          extends: 'IntegrationLogo',
          width: 'C',
          height: 'C',
          minWidth: 'C',
          fontSize: 'B',
          logo: (el) => el.call('marketplaceHostOf')?.installModal?.item?.logo,
          name: (el) =>
            el.call('storeItemText', el.call('marketplaceHostOf')?.installModal?.item, 'name'),
          kind: (el) => el.call('marketplaceHostOf')?.installModal?.item?.kind
        },

        ModalTitle: {
          flow: 'y',
          gap: 'V',

          ModalName: {
            tag: 'h3',
            text: (el) => {
              const host = el.call('marketplaceHostOf')
              return el
                .call('polyglot', 'marketplace.install.title')
                .replace('{name}', el.call('storeItemText', host?.installModal?.item, 'name'))
            },
            fontSize: 'B',
            fontWeight: '700',
            color: 'title',
            margin: '0'
          },

          // "STEP 1 OF 2" — the mono micro-label of the redesign (r044).
          ModalStepLabel: {
            extends: 'AppMicroLabel',
            text: (el) => {
              const host = el.call('marketplaceHostOf')
              const modal = host?.installModal
              const step = modal?.step || 'scope'
              // Apps have NO config step (NextBtn is hidden, InstallBtn shows on
              // the scope step) — so the "Step 1 of 2" label lied. A one-step
              // flow gets a plain label, not a fake 2-step counter.
              if (modal?.item?.kind === 'app')
                return el.call('polyglot', 'marketplace.install.stepChooseWorkspace')
              // When defaultScope is pre-filled, we skip scope step — show single-step label
              if (modal?.defaultScope && step === 'config')
                return el.call('polyglot', 'marketplace.install.stepConfigure')
              if (modal?._catalogError) return el.call('polyglot', 'marketplace.install.stepError')
              return el.call(
                'polyglot',
                step === 'scope'
                  ? 'marketplace.install.stepOneOfTwo'
                  : 'marketplace.install.stepTwoOfTwo'
              )
            }
          }
        }
      },

      ModalCloseBtn: {
        extends: 'CloseButton',
        aria: { label: (el) => el.call('polyglot', 'marketplace.install.closeAria') },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          el.call('closeInstallModal', host)
        }
      }
    },

    // (No divider: the head and the step are set apart by the sheet's `A`
    // gap, not a full-bleed hairline — no rules inside a rounded surface.)

    // Step body — swaps between scope and config.
    ModalBody: {
      flow: 'y',
      gap: 'A',
      flex: '1',

      // Catalog validation error — shown when preselectedKind doesn't exist
      // in catalogue or violates catalogFilter. Replaces both steps.
      MarketplaceInstallCatalogError: {},

      // Step 1: scope selection. Hidden when defaultScope is pre-filled
      // (openInstallableModal pre-validates the scope) or catalog error exists.
      MarketplaceInstallScopeStep: {
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          if (modal.defaultScope) return true // scope step skipped when pre-filled
          return modal.step !== 'scope'
        }
      },

      // Step 2: configuration form.
      MarketplaceInstallConfigStep: {
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          // When defaultScope pre-fills scope, skip directly to config
          if (modal.defaultScope) return modal.step !== 'config'
          return modal.step !== 'config'
        }
      },

      // Error display — shown on any step when an error exists.
      ModalError: {
        tag: 'p',
        padding: 'Y A',
        round: 'radiusControl',
        // The danger surface: `dangerInk` on a wash of itself (was a raw
        // `red.1` fill under raw `red` ink), announced.
        color: 'dangerInk',
        background: 'color-mix(in srgb, currentColor 10%, transparent)',
        role: 'alert',
        fontSize: 'Y',
        margin: '0',
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          return !host?._installModalError
        },
        text: (el) => {
          const host = el.call('marketplaceHostOf')
          return host?._installModalError || ''
        }
      }
    },

    // Footer with navigation / action buttons.
    // The actions (DESIGN_SPEC §5.1): ghost secondaries, the ink primary —
    // the capsule kit's own geometry, never a call-site copy of it (Rule 68).
    // Set apart by the sheet's gap, not a full-bleed hairline.
    ModalFooter: {
      flow: 'x',
      align: 'center flex-end',
      gap: 'Z',
      paddingTop: 'Z',

      // Preview button — opens the gallery detail view for the modal's
      // current item. Moved here from the integration tile rows so the
      // preview action lives alongside the install flow, not duplicated
      // on every row in the list.
      PreviewBtn: {
        extends: 'AppCapsuleBtnGhost',
        text: '{{ marketplace.install.preview | polyglot }}',
        title: (el) => el.call('polyglot', 'marketplace.install.previewTooltip'),
        aria: { label: (el) => el.call('polyglot', 'marketplace.install.previewAria') },
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          return !host?.installModal?.item
        },
        onClick: (e, el) => {
          e.stopPropagation()
          const host = el.call('marketplaceHostOf')
          const item = host?.installModal?.item
          if (!item) return
          el.call('closeInstallModal', host)
          el.call('selectItemFresh', host, item)
        }
      },

      // Cancel button — always visible.
      CancelBtn: {
        extends: 'AppCapsuleBtnGhost',
        text: '{{ marketplace.install.cancel | polyglot }}',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          el.call('closeInstallModal', host)
        }
      },

      // Back button — config step only; hidden when defaultScope pre-fills
      // scope (nowhere to go back to) or when catalog error is shown.
      BackBtn: {
        extends: 'AppCapsuleBtnGhost',
        text: '{{ marketplace.install.back | polyglot }}',
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          if (modal.defaultScope) return true // no scope step to go back to
          return modal.step !== 'config'
        },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          if (!host?.installModal) return
          host.update?.({
            installModal: { ...host.installModal, step: 'scope' },
            _installModalError: null
          })
        }
      },

      // Next button — scope step → config step. Hidden when defaultScope
      // is pre-filled (modal opens directly on config step) or catalog error shown.
      NextBtn: {
        extends: 'AppCapsuleBtnPrimary',
        text: '{{ marketplace.install.next | polyglot }}',
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          // Workspace apps carry no config step (no API keys / config fields)
          // — they install directly from the scope step, so there's no
          // "Next: Configure" to show (the one-click in-shell path).
          if (modal.item?.kind === 'app') return true
          if (modal.defaultScope) return true // scope step is skipped entirely
          if (modal.step !== 'scope') return true
          // Hide if org list is empty (blocked).
          const orgs = _getOrgs(host)
          return orgs.length === 0
        },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf')
          if (!host?.installModal) return
          host.update?.({
            installModal: { ...host.installModal, step: 'config' },
            _installModalError: null
          })
        }
      },

      // Install button — config step only; submits the form.
      // When defaultScope is pre-filled we open directly to config, so
      // this button is visible from the moment the modal opens.
      InstallBtn: {
        extends: 'AppCapsuleBtnPrimary',
        text: (el) => {
          const host = el.call('marketplaceHostOf')
          return el.call(
            'polyglot',
            host?._installModalSaving
              ? 'marketplace.action.installing'
              : 'marketplace.action.install'
          )
        },
        disabled: (el) => {
          const host = el.call('marketplaceHostOf')
          return !!host?._installModalSaving
        },
        hide: (el) => {
          const host = el.call('marketplaceHostOf')
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          // Workspace apps install straight from the scope step (no config
          // step) — the one-click in-shell path the open-flow sets up.
          if (modal.item?.kind === 'app') return false
          if (modal.defaultScope) return modal.step !== 'config'
          return modal.step !== 'config'
        },
        onClick: async (e, el) => {
          const host = el.call('marketplaceHostOf')
          await el.call('submitInstallModal', host, el)
        }
      }
    }
  }
}
